import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtDate, fmtKm } from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Field, Input, Select, Textarea } from "@/components/common/Field";
import { FormDialog } from "@/components/vehicles/VehicleFormDialog";
import { EmptyState } from "@/components/common/Primitives";
import { useRef } from "react";
import { FileText } from "lucide-react";
import { docUrl, FileField, uploadDocument } from "@/lib/uploads";

export function ServiceFormDialog({ open, onClose, vehicleId, onSaved }) {
  const [f, setF] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const fileRef = useRef();
  useEffect(() => {
    if (!open) return;
    setF({ vehicle_id: vehicleId || "", service_type: "Periodic service", service_date: new Date().toISOString().slice(0, 10) });
    if (!vehicleId) api.get("/vehicles").then((r) => setVehicles(r.data));
  }, [open, vehicleId]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const file = fileRef.current?.files?.[0];
    const invoice_document_id = file ? (await uploadDocument(file, { vehicle_id: f.vehicle_id, title: `Invoice · ${f.service_type} · ${f.service_date}`, category: "invoice" })).id : null;
    await api.post("/services", { ...f, odometer_km: num(f.odometer_km), cost: num(f.cost), next_due_km: num(f.next_due_km), next_due_date: f.next_due_date || null, invoice_document_id });
    toast.success("Service recorded"); onSaved();
  };
  return (
    <FormDialog open={open} onClose={onClose} title="Record service" onSubmit={submit} testId="service-form-dialog">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {!vehicleId && <Field label="Vehicle" required className="sm:col-span-2"><Select data-testid="service-vehicle-select" value={f.vehicle_id || ""} onChange={set("vehicle_id")} required><option value="">Select vehicle</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} · {v.registration_no}</option>)}</Select></Field>}
        <Field label="Service type" required><Input data-testid="service-type-input" value={f.service_type || ""} onChange={set("service_type")} required /></Field>
        <Field label="Service date" required><Input data-testid="service-date-input" type="date" value={f.service_date || ""} onChange={set("service_date")} required /></Field>
        <Field label="Odometer at service (km)"><Input data-testid="service-odometer-input" type="number" value={f.odometer_km || ""} onChange={set("odometer_km")} /></Field>
        <Field label="Cost (₹)"><Input data-testid="service-cost-input" type="number" value={f.cost || ""} onChange={set("cost")} /></Field>
        <Field label="Vendor / garage" className="sm:col-span-2"><Input data-testid="service-vendor-input" value={f.vendor || ""} onChange={set("vendor")} /></Field>
        <Field label="Next due date"><Input data-testid="service-next-date-input" type="date" value={f.next_due_date || ""} onChange={set("next_due_date")} /></Field>
        <Field label="Next due km"><Input data-testid="service-next-km-input" type="number" value={f.next_due_km || ""} onChange={set("next_due_km")} /></Field>
        <FileField label="Invoice (photo / PDF, optional)" inputRef={fileRef} testId="service-invoice-input" />
        <Field label="Notes" className="sm:col-span-2"><Textarea data-testid="service-notes-input" value={f.notes || ""} onChange={set("notes")} /></Field>
      </div>
    </FormDialog>
  );
}

export function ServiceList({ records, showVehicle }) {
  if (records.length === 0) return <EmptyState testId="services-empty" title="No service records" description="Record a service to start tracking next-due by date and km." />;
  return (
    <div className="fv-card divide-y p-1" data-testid="services-list">
      {records.map((r) => (
        <div key={r.id} className="fv-dense-row cursor-default" data-testid={`service-row-${r.id}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{showVehicle && <span>{r.vehicle_name || r.registration_no} · </span>}{r.service_type}<span className="font-normal text-muted-foreground"> · {fmtDate(r.service_date)} · {fmtKm(r.odometer_km)}</span></p>
            <p className="truncate text-[11px] text-muted-foreground">
              {showVehicle && r.registration_no ? `${r.registration_no} · ` : ""}{r.vendor || "—"}{r.cost != null ? ` · ₹${Number(r.cost).toLocaleString("en-IN")}` : ""} · Next due {r.next_due_date ? fmtDate(r.next_due_date) : "—"}{r.next_due_km ? ` / ${fmtKm(r.next_due_km)}` : ""}
              {r.km_to_due != null ? ` (${r.km_to_due > 0 ? `${Math.round(r.km_to_due)} km left` : `${Math.round(-r.km_to_due)} km over`})` : ""}
            </p>
          </div>
          {r.invoice_document_id && <a href={docUrl(r.invoice_document_id)} target="_blank" rel="noreferrer" data-testid={`service-invoice-${r.id}`} className="rounded-lg p-1.5 text-primary hover:bg-primary/10" aria-label="Invoice"><FileText className="h-4 w-4" /></a>}
          <StatusBadge status={r.due_status} testId={`service-due-badge-${r.id}`} />
        </div>
      ))}
    </div>
  );
}
